import React from 'react';

import {
  ComboBoxMultiselectParam,
  Input,
  Option,
  RequiredHelpPhrase,
  Row,
  Select,
  SelectParam,
  SubmitButton,
  Textarea,
  TextAreaParam,
  TextParam,
} from '../../../_lib/Form2';
import { ListBoxModifier } from '../../molecules/Form/components/ComboBox';
import { UserWithRolesSelect } from '../../molecules/Form/components/UserWithRolesSelect';
import { FetchState } from '../../../hooks/reducers/useFetchReducer';
import { AxiosError, AxiosResponse } from 'axios';
import { SonResponseError } from '../../../_lib/Form2/types/SonResponseError';
import { mapTeamroomInviteError } from '../../../_lib/Form2/utils/teamroomInviteErrorMapper';
import { useTranslation } from 'react-i18next';
import { Namespace } from '../../../translation/namespace';
import { RowModifier } from '../../../_lib/Form2/grid/Row/Row';

type FormParams = {
  getByName: <T = unknown>(name: string) => T | undefined;
  findByName: <T = unknown>(name: string) => T | undefined;
};

type UserInviteFormTemplateType = {
  headline?: string;
  translationNamespace?: string;
  params: FormParams;
  fetchState: FetchState<AxiosResponse<unknown>, AxiosError<SonResponseError>>;
  userMail?: {
    label: string;
    helpText: string;
    patternMismatchErrorMessage: string;
  };
  userSelect?: {
    label: string;
    helpText: string;
    validationMessage: string;
  };
  message: {
    label: string;
  };
  roleSelect?: {
    label: string;
  };
  submit: {
    name: string;
  };
};

export default function UserInviteFormTemplate({
  headline,
  userMail,
  userSelect,
  roleSelect,
  message,
  submit,
  params,
  fetchState,
}: UserInviteFormTemplateType) {
  const userIdParam = params.findByName('userId') as ComboBoxMultiselectParam<'userId'> | undefined;
  const emailParam = params.findByName('email') as TextParam<'email'> | undefined;
  const invitationTextParam = params.getByName('invitationText') as TextAreaParam<'invitationText'> | undefined;
  const roleIdParam = params.getByName('roleId') as SelectParam<'roleId'> | undefined;

  const isMultiUserMode = !!userIdParam;
  const { t } = useTranslation();

  const customErrorResponseMapper = (response: AxiosResponse) => mapTeamroomInviteError(response, t);

  return (
    <>
      {headline && <h3 className="form__headline">{headline}</h3>}
      <RequiredHelpPhrase />
      <Row modifier={isMultiUserMode ? RowModifier.BLOCK : RowModifier.GRID_2_COLUMNS}>
        {isMultiUserMode && roleIdParam ? (
          <UserWithRolesSelect
            idPrefix="invite-user-"
            roleParam={roleIdParam}
            suggestUrl={userIdParam.comboBoxUrl.url}
            modifierListBox={ListBoxModifier.RELATIVE}
            labelTitle={userSelect?.label || ''}
            required={userIdParam.required}
          />
        ) : (
          <>
            {emailParam && userMail && (
              <Input type="email" label={userMail.label} helpText={userMail.helpText} param={emailParam} />
            )}
            {roleIdParam && (
              <Select label={roleSelect?.label || ''} param={roleIdParam} defaultValue={roleIdParam.value}>
                {roleIdParam.options?.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.text}
                  </Option>
                ))}
              </Select>
            )}
          </>
        )}
      </Row>
      {invitationTextParam && (
        <Row>
          <Textarea label={message.label} param={invitationTextParam} />
        </Row>
      )}
      <SubmitButton
        buttonLabel={submit.name}
        messageSuccess={t(`${Namespace.TeamroomManagement}:actions.invite.inviteSuccess`)}
        messageSending={t(`${Namespace.TeamroomManagement}:actions.invite.inviteSending`)}
        messageFail={t(`${Namespace.TeamroomManagement}:actions.invite.inviteFail`)}
        fetchState={fetchState}
        customErrorTypeMessages={{
          personsNotPrivileged: t(`${Namespace.TeamroomManagement}:actions.invite.status.personsNotPrivileged`),
          teamroomInvitePartiallyFailed: t(`${Namespace.TeamroomManagement}:error.teamroomInvitePartiallyFailed.base`),
        }}
        customErrorResponseMapper={customErrorResponseMapper}
      />
    </>
  );
}
